import React from "react";
import { useParams } from "react-router-dom";
import { usePaymentLink } from "../../hooks/usePaymentLink";
import { Sidebar } from "../../components/pay/Sidebar";
import { CheckoutPanel } from "../../components/pay/CheckoutPanel";
import { ComingSoonFooter } from "../../components/pay/ComingSoonFooter";
import { QRCodeToggle } from "../../components/pay/QRCodeToggle";
import { NotFoundPage } from "./NotFoundPage";
import { FaLock } from "react-icons/fa";

/**
 * Generic payment page that handles all three route shapes.
 *
 * Not exported directly — each route uses a tiny wrapper below that
 * feeds PaymentPage the right `params` object for usePaymentLink.
 * Keeps the three routes in App.js trivially short + lets the
 * usePaymentLink hook's discriminated union stay tight.
 *
 * Layout:
 *   desktop: [Sidebar 40%][Main 60%]
 *   mobile:  compact sidebar bar on top, main stacked below
 *
 * Main column sections (top to bottom):
 *   - CheckoutPanel — single-flow checkout (connect → pay → receipt)
 *     that owns the x402 vs direct method selection internally
 *   - QR code toggle (open on mobile)
 *   - ComingSoonFooter — "Card payments coming soon" one-liner
 *   - Security footer
 */
function PaymentPage({ params }) {
  const { status, link, error, reason, refetch } = usePaymentLink(params);

  if (status === "loading") {
    return (
      <div className="pay-loading">
        <div className="pay-loading__spinner" />
        <div style={{ color: "#6e6e73", fontSize: 14 }}>
          Loading payment link…
        </div>
      </div>
    );
  }

  if (status === "not-found") {
    return <NotFoundPage message={error?.message} />;
  }

  if (status === "gone") {
    // Dedicated "this link is no longer available" screen. When the
    // 410 was an owner-username-changed (HNS rename), we show the
    // current URL as a rescue link so a user who bookmarked the old
    // slug can jump to the new one with one click. Other 410s
    // (expired / disabled / maxed) just get the message.
    const isRenamed = reason === "owner_username_changed";
    const currentUrl = error?.currentUrl;
    // LinkOwnershipChangedError carries the old + current @username
    // directly from the backend's 410 body. Using them explicitly
    // in the copy ("renamed from @X to @Y") is much clearer than
    // the previous generic "this link has moved" — users recognize
    // it as an ownership-change story rather than a broken URL.
    const oldUsername = error?.oldUsername;
    const currentUsername = error?.currentUsername;
    const shortUrl = error?.shortUrl;
    return (
      <div className="pay-gone">
        <div style={{ fontSize: 48 }} aria-hidden="true">⏳</div>
        <h1 className="pay-gone__title">
          {isRenamed
            ? "This payment link is no longer valid"
            : "Payment link unavailable"}
        </h1>
        {isRenamed && oldUsername && currentUsername ? (
          <p className="pay-gone__note">
            The owner of this link has changed their username from{" "}
            <strong>@{oldUsername}</strong> to{" "}
            <strong>@{currentUsername}</strong>. Use the updated link below to
            continue.
          </p>
        ) : isRenamed && oldUsername ? (
          <p className="pay-gone__note">
            The owner of this link (<strong>@{oldUsername}</strong>) has
            released their @username. The link's permanent URL still works —
            try it below.
          </p>
        ) : (
          <p className="pay-gone__note">{error?.message}</p>
        )}
        {isRenamed && currentUrl && (
          <a
            href={currentUrl}
            rel="noopener noreferrer"
            className="pay-gone__link"
          >
            Open the current link →
          </a>
        )}
        {isRenamed && !currentUrl && (error?.fallbackUrl || shortUrl) && (
          <a
            href={error?.fallbackUrl || shortUrl}
            rel="noopener noreferrer"
            className="pay-gone__link"
          >
            Try the permanent URL →
          </a>
        )}
        {!isRenamed && error?.fallbackUrl && (
          <a
            href={error.fallbackUrl}
            rel="noopener noreferrer"
            className="pay-gone__link"
          >
            Try the permanent URL →
          </a>
        )}
      </div>
    );
  }

  if (status === "error") {
    return (
      <div className="pay-not-found">
        <div style={{ fontSize: 48 }} aria-hidden="true">⚠️</div>
        <h1 className="pay-not-found__title">Couldn't load this link</h1>
        <p className="pay-not-found__note">
          {error?.message ||
            "Network error. Please check your connection and try again."}
        </p>
        <button type="button" className="pay-retry-btn" onClick={refetch}>
          Try again
        </button>
      </div>
    );
  }

  return (
    <div className="pay-shell">
      <Sidebar link={link} />
      <main className="pay-main">
        <CheckoutPanel link={link} />

        <div style={{ marginTop: 24 }}>
          <QRCodeToggle />
        </div>

        <ComingSoonFooter />

        <div className="pay-security">
          <FaLock size={14} />
          You sign an authorization; horuswallet never sees your private key.
        </div>
      </main>
    </div>
  );
}

// ─── Route-specific wrappers ─────────────────────────────────

/** Short-code route wrapper: /pay/p/:code */
export function ShortCodePayPage() {
  const { code } = useParams();
  return <PaymentPage params={{ type: "code", code }} />;
}

/** Slug route wrapper: /pay/:username/:slug
 *
 * The route pattern doesn't include a literal `@` — the user-visible
 * URL still shows `/pay/@alice/coffee`, but React Router captures
 * `@alice` (with the leading `@`) as the `username` param. The
 * downstream `fetchLinkInfoBySlug` strips the leading `@` so both
 * `/pay/@alice/coffee` and `/pay/alice/coffee` resolve to the same
 * link — and browsers that percent-encode `@` in the path no longer
 * fall through to a blank page. */
export function SlugPayPage() {
  const { username, slug } = useParams();
  return <PaymentPage params={{ type: "slug", username, slug }} />;
}

/** UUID route wrapper: /pay/link/:linkId (backward compat for the
 *  legacy UUID URL shape — still the source-of-record on the
 *  backend, and some agents paste it directly). */
export function UuidPayPage() {
  const { linkId } = useParams();
  return <PaymentPage params={{ type: "id", linkId }} />;
}
